import { Sentence } from '../../../types/types';

// Phrases de construction pour Kama. Chaque `hieroglyphs`/`hieroglyphWords`
// copie tel quel le glyphe déjà confirmé d'une fiche existante du corpus
// (categories/phrase.ts, categories/nourritures.ts, categories/animaux.ts,
// categories/temps.ts, categories/villes.ts) — jamais un codepoint deviné
// (voir la skill kama-hieroglyph-decoder). `hieroglyphWords[i]` correspond
// toujours à `words[i]` (même ordre, même longueur) et leur concaténation
// reconstitue exactement `hieroglyphs`. Les tuiles du jeu (`words`) restent
// la translittération pour le score (SentenceConstructionGame.tsx compare
// `.text`) ; c'est `hieroglyphWords` qui pilote l'affichage glyphique de
// chaque tuile (`.displayText`), la translittération n'apparaissant plus
// qu'en légende. `category` place chaque phrase sous une unité précise du
// sentier (UNIT_SENTENCE_CATEGORIES dans CategorySelection.tsx, comparaison
// stricte) : 'Salutations', 'Présentation', 'Famille et relations' → unité 1 ;
// 'Vie quotidienne', 'Au marché' → unité 2 ; 'Voyages' → unité 3 ;
// 'Panthéon et royaume' → unité 4.
export const sentencesToConstruct: Sentence[] = [
  // ── Unité 1 (Premiers pas) — reprend phrase.ts (ordre des mots, prépositions,
  // phrase adverbiale/nominale, adjectif épithète, pronom indépendant, génitif).
  {
    original: 'sḏm zẖꜣw mdt',
    french: 'Le scribe entend les mots',
    english: 'The scribe hears the words',
    words: ['sḏm', 'zẖꜣw', 'mdt'],
    hieroglyphs: '𓋴𓆓𓅓𓄔𓏞𓅱𓀀𓌃𓏏',
    hieroglyphWords: ['𓋴𓆓𓅓𓄔', '𓏞𓅱𓀀', '𓌃𓏏'],
    category: 'Salutations',
    tags: ['ordre des mots', 'verbe-sujet-objet'],
  },
  {
    original: 'sḏm nsw mdt',
    french: 'Le roi entend les mots',
    english: 'The king hears the words',
    words: ['sḏm', 'nsw', 'mdt'],
    hieroglyphs: '𓋴𓆓𓅓𓄔𓇓𓌃𓏏',
    hieroglyphWords: ['𓋴𓆓𓅓𓄔', '𓇓', '𓌃𓏏'],
    category: 'Salutations',
    tags: ['ordre des mots', 'verbe-sujet-objet'],
  },
  {
    original: 'm pr',
    french: 'Dans la maison',
    english: 'In the house',
    words: ['m', 'pr'],
    hieroglyphs: '𓅓𓉐',
    hieroglyphWords: ['𓅓', '𓉐'],
    category: 'Salutations',
    tags: ['préposition'],
  },
  {
    original: 'n nṯr',
    french: 'Pour le dieu',
    english: 'For the god',
    words: ['n', 'nṯr'],
    hieroglyphs: '𓈖𓊹',
    hieroglyphWords: ['𓈖', '𓊹'],
    category: 'Salutations',
    tags: ['préposition'],
  },
  {
    original: 'nsw m pr ꜥꜣ',
    french: 'Le roi est dans le palais',
    english: 'The king is in the palace',
    words: ['nsw', 'm', 'pr', 'ꜥꜣ'],
    hieroglyphs: '𓇓𓅓𓉐𓉻',
    hieroglyphWords: ['𓇓', '𓅓', '𓉐', '𓉻'],
    category: 'Salutations',
    tags: ['phrase adverbiale'],
  },
  {
    original: 'nṯr m pr',
    french: 'Le dieu est dans la maison',
    english: 'The god is in the house',
    words: ['nṯr', 'm', 'pr'],
    hieroglyphs: '𓊹𓅓𓉐',
    hieroglyphWords: ['𓊹', '𓅓', '𓉐'],
    category: 'Salutations',
    tags: ['phrase adverbiale'],
  },
  {
    original: 'zẖꜣw pw',
    french: "C'est un scribe",
    english: 'He is a scribe',
    words: ['zẖꜣw', 'pw'],
    hieroglyphs: '𓏞𓅱𓀀𓊪𓅱',
    hieroglyphWords: ['𓏞𓅱𓀀', '𓊪𓅱'],
    category: 'Salutations',
    tags: ['phrase nominale'],
  },
  {
    original: 'nṯr pw',
    french: "C'est un dieu",
    english: 'It is a god',
    words: ['nṯr', 'pw'],
    hieroglyphs: '𓊹𓊪𓅱',
    hieroglyphWords: ['𓊹', '𓊪𓅱'],
    category: 'Salutations',
    tags: ['phrase nominale'],
  },
  {
    original: 'nṯr nfr',
    french: 'Le bon dieu',
    english: 'The good god',
    words: ['nṯr', 'nfr'],
    hieroglyphs: '𓊹𓄤',
    hieroglyphWords: ['𓊹', '𓄤'],
    category: 'Salutations',
    tags: ['adjectif épithète'],
  },
  {
    original: 'nṯr ꜥꜣ',
    french: 'Le grand dieu',
    english: 'The great god',
    words: ['nṯr', 'ꜥꜣ'],
    hieroglyphs: '𓊹𓉻',
    hieroglyphWords: ['𓊹', '𓉻'],
    category: 'Salutations',
    tags: ['adjectif épithète'],
  },
  {
    original: 'ink zẖꜣw',
    french: 'Moi, je suis scribe',
    english: 'I am a scribe',
    words: ['ink', 'zẖꜣw'],
    hieroglyphs: '𓇋𓈖𓂓𓏞𓅱𓀀',
    hieroglyphWords: ['𓇋𓈖𓂓', '𓏞𓅱𓀀'],
    category: 'Salutations',
    tags: ['pronom indépendant'],
  },
  {
    original: 'pr nsw',
    french: 'La maison du roi',
    english: "The king's house",
    words: ['pr', 'nsw'],
    hieroglyphs: '𓉐𓇓',
    hieroglyphWords: ['𓉐', '𓇓'],
    category: 'Salutations',
    tags: ['génitif direct'],
  },
  {
    original: 'pr nṯr',
    french: 'Le temple',
    english: 'The temple',
    words: ['pr', 'nṯr'],
    hieroglyphs: '𓉐𓊹',
    hieroglyphWords: ['𓉐', '𓊹'],
    category: 'Salutations',
    tags: ['génitif direct'],
  },
  {
    original: 'zẖꜣw n nsw',
    french: 'Le scribe du roi',
    english: "The king's scribe",
    words: ['zẖꜣw', 'n', 'nsw'],
    hieroglyphs: '𓏞𓅱𓀀𓈖𓇓',
    hieroglyphWords: ['𓏞𓅱𓀀', '𓈖', '𓇓'],
    category: 'Salutations',
    tags: ['génitif indirect'],
  },
  {
    original: 'mdt n nsw',
    french: 'Les mots du roi',
    english: "The king's words",
    words: ['mdt', 'n', 'nsw'],
    hieroglyphs: '𓌃𓏏𓈖𓇓',
    hieroglyphWords: ['𓌃𓏏', '𓈖', '𓇓'],
    category: 'Salutations',
    tags: ['génitif indirect'],
  },

  // ── Unité 2 (Vie quotidienne) — vocabulaire de Nourriture et Animaux.
  {
    original: 'mw pw',
    french: "C'est de l'eau",
    english: 'It is water',
    words: ['mw', 'pw'],
    hieroglyphs: '𓈖𓊪𓅱',
    hieroglyphWords: ['𓈖', '𓊪𓅱'],
    category: 'Vie quotidienne',
    tags: ['phrase nominale', 'nourriture'],
  },
  {
    original: 't pw',
    french: "C'est du pain",
    english: 'It is bread',
    words: ['t', 'pw'],
    hieroglyphs: '𓏏𓊪𓅱',
    hieroglyphWords: ['𓏏', '𓊪𓅱'],
    category: 'Vie quotidienne',
    tags: ['phrase nominale', 'nourriture'],
  },
  {
    original: 'sbk pw',
    french: "C'est un crocodile",
    english: 'It is a crocodile',
    words: ['sbk', 'pw'],
    hieroglyphs: '𓆊𓊪𓅱',
    hieroglyphWords: ['𓆊', '𓊪𓅱'],
    category: 'Vie quotidienne',
    tags: ['phrase nominale', 'animaux'],
  },
  {
    original: 'sbk ꜥꜣ',
    french: 'Le grand crocodile',
    english: 'The great crocodile',
    words: ['sbk', 'ꜥꜣ'],
    hieroglyphs: '𓆊𓉻',
    hieroglyphWords: ['𓆊', '𓉻'],
    category: 'Vie quotidienne',
    tags: ['adjectif épithète', 'animaux'],
  },
  {
    original: 'jf nfr',
    french: 'La bonne viande',
    english: 'The good meat',
    words: ['jf', 'nfr'],
    hieroglyphs: '𓄑𓄔𓄤',
    hieroglyphWords: ['𓄑𓄔', '𓄤'],
    category: 'Vie quotidienne',
    tags: ['adjectif épithète', 'nourriture'],
  },
  {
    original: 'n rmt',
    french: 'Pour le poisson',
    english: 'For the fish',
    words: ['n', 'rmt'],
    hieroglyphs: '𓈖𓆠',
    hieroglyphWords: ['𓈖', '𓆠'],
    category: 'Vie quotidienne',
    tags: ['préposition', 'nourriture'],
  },
  {
    original: 'n mw',
    french: "Pour l'eau",
    english: 'For the water',
    words: ['n', 'mw'],
    hieroglyphs: '𓈖𓈖',
    hieroglyphWords: ['𓈖', '𓈖'],
    category: 'Vie quotidienne',
    tags: ['préposition', 'nourriture'],
  },
  {
    original: 'pr sbk',
    french: 'La maison du crocodile',
    english: "The crocodile's house",
    words: ['pr', 'sbk'],
    hieroglyphs: '𓉐𓆊',
    hieroglyphWords: ['𓉐', '𓆊'],
    category: 'Vie quotidienne',
    tags: ['génitif direct', 'animaux'],
  },

  // ── Unité 3 (Explorer) — vocabulaire de Temps et le nom du Nil (Villes).
  {
    original: 'itrw ꜥꜣ',
    french: 'Le Nil (litt. la grande eau)',
    english: 'The Nile (lit. the great water)',
    words: ['itrw', 'ꜥꜣ'],
    hieroglyphs: '𓇋𓏏𓂋𓏺𓈗𓈘𓈇𓉻𓏛',
    hieroglyphWords: ['𓇋𓏏𓂋𓏺𓈗𓈘𓈇', '𓉻𓏛'],
    category: 'Voyages',
    tags: ['génitif direct', 'géographie'],
  },
  {
    original: 'm itrw',
    french: 'Dans le fleuve',
    english: 'In the river',
    words: ['m', 'itrw'],
    hieroglyphs: '𓅓𓇋𓏏𓂋𓏺𓈗𓈘𓈇',
    hieroglyphWords: ['𓅓', '𓇋𓏏𓂋𓏺𓈗𓈘𓈇'],
    category: 'Voyages',
    tags: ['préposition', 'géographie'],
  },
  {
    original: 'hrw nfr',
    french: 'Le bon jour',
    english: 'The good day',
    words: ['hrw', 'nfr'],
    hieroglyphs: '𓉔𓅱𓇳𓄤',
    hieroglyphWords: ['𓉔𓅱𓇳', '𓄤'],
    category: 'Voyages',
    tags: ['adjectif épithète', 'temps'],
  },
  {
    original: 'hrw pw',
    french: "C'est le jour",
    english: 'It is day',
    words: ['hrw', 'pw'],
    hieroglyphs: '𓉔𓅱𓇳𓊪𓅱',
    hieroglyphWords: ['𓉔𓅱𓇳', '𓊪𓅱'],
    category: 'Voyages',
    tags: ['phrase nominale', 'temps'],
  },
  {
    original: 'rnp.t nfr',
    french: 'La bonne année',
    english: 'The good year',
    words: ['rnp.t', 'nfr'],
    hieroglyphs: '𓆳𓏏𓏤𓄤',
    hieroglyphWords: ['𓆳𓏏𓏤', '𓄤'],
    category: 'Voyages',
    tags: ['adjectif épithète', 'temps'],
  },
  {
    original: 'n itrw',
    french: 'Pour le fleuve',
    english: 'For the river',
    words: ['n', 'itrw'],
    hieroglyphs: '𓈖𓇋𓏏𓂋𓏺𓈗𓈘𓈇',
    hieroglyphWords: ['𓈖', '𓇋𓏏𓂋𓏺𓈗𓈘𓈇'],
    category: 'Voyages',
    tags: ['préposition', 'géographie'],
  },
  // ── Phrases supplémentaires — mêmes règles que ci-dessus : chaque groupe
  // de glyphes est copié d'une fiche confirmée (famille.ts, nourritures.ts,
  // animaux.ts, villes.ts, divinites.ts, temps.ts, lecture.ts, phrase.ts) ;
  // un nom suivi d'un suffixe (it.f, mwt.s, pr.f) forme une seule tuile,
  // le suffixe s'écrivant après le déterminatif. nfrt = nfr + t (X1),
  // accord féminin de l'adjectif après niwt.
  {
    original: 'ink it',
    french: 'Moi, je suis père',
    english: 'I am a father',
    words: ['ink', 'it'],
    hieroglyphs: '𓇋𓈖𓂓𓇋𓏏𓀀',
    hieroglyphWords: ['𓇋𓈖𓂓', '𓇋𓏏𓀀'],
    category: 'Présentation',
    tags: ['pronom indépendant', 'famille'],
  },
  {
    original: 'ink mwt',
    french: 'Moi, je suis mère',
    english: 'I am a mother',
    words: ['ink', 'mwt'],
    hieroglyphs: '𓇋𓈖𓂓𓅐𓏏𓁐',
    hieroglyphWords: ['𓇋𓈖𓂓', '𓅐𓏏𓁐'],
    category: 'Présentation',
    tags: ['pronom indépendant', 'famille'],
  },
  {
    original: 'it.f pw',
    french: 'C\'est son père',
    english: 'It is his father',
    words: ['it.f', 'pw'],
    hieroglyphs: '𓇋𓏏𓀀𓆑𓊪𓅱',
    hieroglyphWords: ['𓇋𓏏𓀀𓆑', '𓊪𓅱'],
    category: 'Famille et relations',
    tags: ['pronom suffixe', 'phrase nominale'],
  },
  {
    original: 'mwt.s m pr',
    french: 'Sa mère est dans la maison',
    english: 'Her mother is in the house',
    words: ['mwt.s', 'm', 'pr'],
    hieroglyphs: '𓅐𓏏𓁐𓋴𓅓𓉐',
    hieroglyphWords: ['𓅐𓏏𓁐𓋴', '𓅓', '𓉐'],
    category: 'Famille et relations',
    tags: ['pronom suffixe', 'phrase adverbiale'],
  },
  {
    original: 'sn pw',
    french: 'C\'est un frère',
    english: 'It is a brother',
    words: ['sn', 'pw'],
    hieroglyphs: '𓋴𓈖𓀀𓊪𓅱',
    hieroglyphWords: ['𓋴𓈖𓀀', '𓊪𓅱'],
    category: 'Famille et relations',
    tags: ['phrase nominale', 'famille'],
  },
  {
    original: 'sꜣ nsw',
    french: 'Le fils du roi',
    english: 'The king\'s son',
    words: ['sꜣ', 'nsw'],
    hieroglyphs: '𓅭𓀀𓇓',
    hieroglyphWords: ['𓅭𓀀', '𓇓'],
    category: 'Famille et relations',
    tags: ['génitif direct', 'titre'],
  },
  {
    original: 'sꜣt nsw',
    french: 'La fille du roi',
    english: 'The king\'s daughter',
    words: ['sꜣt', 'nsw'],
    hieroglyphs: '𓅭𓏏𓁐𓇓',
    hieroglyphWords: ['𓅭𓏏𓁐', '𓇓'],
    category: 'Famille et relations',
    tags: ['génitif direct', 'titre'],
  },
  {
    original: 'ḥmt nsw',
    french: 'L\'épouse du roi',
    english: 'The king\'s wife',
    words: ['ḥmt', 'nsw'],
    hieroglyphs: '𓈞𓏏𓁐𓇓',
    hieroglyphWords: ['𓈞𓏏𓁐', '𓇓'],
    category: 'Famille et relations',
    tags: ['génitif direct', 'titre'],
  },
  {
    original: 'mwt nṯr',
    french: 'La mère du dieu',
    english: 'The god\'s mother',
    words: ['mwt', 'nṯr'],
    hieroglyphs: '𓅐𓏏𓁐𓊹',
    hieroglyphWords: ['𓅐𓏏𓁐', '𓊹'],
    category: 'Famille et relations',
    tags: ['génitif direct', 'famille'],
  },
  {
    original: 'sꜣ n zẖꜣw',
    french: 'Le fils du scribe',
    english: 'The scribe\'s son',
    words: ['sꜣ', 'n', 'zẖꜣw'],
    hieroglyphs: '𓅭𓀀𓈖𓏞𓅱𓀀',
    hieroglyphWords: ['𓅭𓀀', '𓈖', '𓏞𓅱𓀀'],
    category: 'Famille et relations',
    tags: ['génitif indirect', 'famille'],
  },
  {
    original: 'ink sꜣt nsw',
    french: 'Moi, je suis la fille du roi',
    english: 'I am the king\'s daughter',
    words: ['ink', 'sꜣt', 'nsw'],
    hieroglyphs: '𓇋𓈖𓂓𓅭𓏏𓁐𓇓',
    hieroglyphWords: ['𓇋𓈖𓂓', '𓅭𓏏𓁐', '𓇓'],
    category: 'Présentation',
    tags: ['pronom indépendant', 'génitif direct'],
  },
  {
    original: 'ḥnqt pw',
    french: 'C\'est de la bière',
    english: 'It is beer',
    words: ['ḥnqt', 'pw'],
    hieroglyphs: '𓎛𓈖𓏏𓊪𓅱',
    hieroglyphWords: ['𓎛𓈖𓏏', '𓊪𓅱'],
    category: 'Au marché',
    tags: ['phrase nominale', 'nourriture'],
  },
  {
    original: 'jrp pw',
    french: 'C\'est du vin',
    english: 'It is wine',
    words: ['jrp', 'pw'],
    hieroglyphs: '𓇋𓂋𓊪𓊪𓅱',
    hieroglyphWords: ['𓇋𓂋𓊪', '𓊪𓅱'],
    category: 'Au marché',
    tags: ['phrase nominale', 'nourriture'],
  },
  {
    original: 'jrp nfr',
    french: 'Le bon vin',
    english: 'The good wine',
    words: ['jrp', 'nfr'],
    hieroglyphs: '𓇋𓂋𓊪𓄤',
    hieroglyphWords: ['𓇋𓂋𓊪', '𓄤'],
    category: 'Au marché',
    tags: ['adjectif', 'nourriture'],
  },
  {
    original: 'jwꜣ pw',
    french: 'C\'est un bœuf',
    english: 'It is an ox',
    words: ['jwꜣ', 'pw'],
    hieroglyphs: '𓃒𓊪𓅱',
    hieroglyphWords: ['𓃒', '𓊪𓅱'],
    category: 'Vie quotidienne',
    tags: ['phrase nominale', 'animaux'],
  },
  {
    original: 'mꜣj ꜥꜣ',
    french: 'Le grand lion',
    english: 'The great lion',
    words: ['mꜣj', 'ꜥꜣ'],
    hieroglyphs: '𓄿𓇋𓃬𓉻',
    hieroglyphWords: ['𓄿𓇋𓃬', '𓉻'],
    category: 'Vie quotidienne',
    tags: ['adjectif', 'animaux'],
  },
  {
    original: 't m pr',
    french: 'Le pain est dans la maison',
    english: 'The bread is in the house',
    words: ['t', 'm', 'pr'],
    hieroglyphs: '𓏏𓅓𓉐',
    hieroglyphWords: ['𓏏', '𓅓', '𓉐'],
    category: 'Au marché',
    tags: ['phrase adverbiale', 'nourriture'],
  },
  {
    original: 'mjw m pr',
    french: 'Le chat est dans la maison',
    english: 'The cat is in the house',
    words: ['mjw', 'm', 'pr'],
    hieroglyphs: '𓏇𓇋𓅱𓃠𓅓𓉐',
    hieroglyphWords: ['𓏇𓇋𓅱𓃠', '𓅓', '𓉐'],
    category: 'Vie quotidienne',
    tags: ['phrase adverbiale', 'animaux'],
  },
  {
    original: 'sbk m mw',
    french: 'Le crocodile est dans l\'eau',
    english: 'The crocodile is in the water',
    words: ['sbk', 'm', 'mw'],
    hieroglyphs: '𓆊𓅓𓈖',
    hieroglyphWords: ['𓆊', '𓅓', '𓈖'],
    category: 'Vie quotidienne',
    tags: ['phrase adverbiale', 'animaux'],
  },
  {
    original: 'ḥnqt n nṯr',
    french: 'De la bière pour le dieu',
    english: 'Beer for the god',
    words: ['ḥnqt', 'n', 'nṯr'],
    hieroglyphs: '𓎛𓈖𓏏𓈖𓊹',
    hieroglyphWords: ['𓎛𓈖𓏏', '𓈖', '𓊹'],
    category: 'Au marché',
    tags: ['préposition', 'offrande'],
  },
  {
    original: 'jf n nsw',
    french: 'De la viande pour le roi',
    english: 'Meat for the king',
    words: ['jf', 'n', 'nsw'],
    hieroglyphs: '𓄑𓄔𓈖𓇓',
    hieroglyphWords: ['𓄑𓄔', '𓈖', '𓇓'],
    category: 'Au marché',
    tags: ['préposition', 'nourriture'],
  },
  {
    original: 'pr.f nfr',
    french: 'Sa belle maison',
    english: 'His beautiful house',
    words: ['pr.f', 'nfr'],
    hieroglyphs: '𓉐𓆑𓄤',
    hieroglyphWords: ['𓉐𓆑', '𓄤'],
    category: 'Vie quotidienne',
    tags: ['pronom suffixe', 'adjectif'],
  },
  {
    original: 'nsw m Wꜣst',
    french: 'Le roi est à Thèbes',
    english: 'The king is in Thebes',
    words: ['nsw', 'm', 'Wꜣst'],
    hieroglyphs: '𓇓𓅓𓋆𓏏𓊖',
    hieroglyphWords: ['𓇓', '𓅓', '𓋆𓏏𓊖'],
    category: 'Voyages',
    tags: ['phrase adverbiale', 'villes'],
  },
  {
    original: 'zẖꜣw m Mn-nfr',
    french: 'Le scribe est à Memphis',
    english: 'The scribe is in Memphis',
    words: ['zẖꜣw', 'm', 'Mn-nfr'],
    hieroglyphs: '𓏞𓅱𓀀𓅓𓏠𓈖𓄤𓆑𓂋𓉴𓊖',
    hieroglyphWords: ['𓏞𓅱𓀀', '𓅓', '𓏠𓈖𓄤𓆑𓂋𓉴𓊖'],
    category: 'Voyages',
    tags: ['phrase adverbiale', 'villes'],
  },
  {
    original: 'nṯr m Jwnw',
    french: 'Le dieu est à Héliopolis',
    english: 'The god is in Heliopolis',
    words: ['nṯr', 'm', 'Jwnw'],
    hieroglyphs: '𓊹𓅓𓉺𓏌𓊖',
    hieroglyphWords: ['𓊹', '𓅓', '𓉺𓏌𓊖'],
    category: 'Voyages',
    tags: ['phrase adverbiale', 'villes'],
  },
  {
    original: 'nsw m niwt',
    french: 'Le roi est dans la ville',
    english: 'The king is in the city',
    words: ['nsw', 'm', 'niwt'],
    hieroglyphs: '𓇓𓅓𓊖',
    hieroglyphWords: ['𓇓', '𓅓', '𓊖'],
    category: 'Voyages',
    tags: ['phrase adverbiale', 'villes'],
  },
  {
    original: 'niwt nfrt',
    french: 'La belle ville',
    english: 'The beautiful city',
    words: ['niwt', 'nfrt'],
    hieroglyphs: '𓊖𓄤𓏏',
    hieroglyphWords: ['𓊖', '𓄤𓏏'],
    category: 'Voyages',
    tags: ['adjectif', 'féminin'],
  },
  {
    original: 'Km.t pw',
    french: 'C\'est l\'Égypte',
    english: 'It is Egypt',
    words: ['Km.t', 'pw'],
    hieroglyphs: '𓆎𓅓𓏏𓊖𓊪𓅱',
    hieroglyphWords: ['𓆎𓅓𓏏𓊖', '𓊪𓅱'],
    category: 'Voyages',
    tags: ['phrase nominale', 'géographie'],
  },
  {
    original: 'sbk m itrw',
    french: 'Le crocodile est dans le fleuve',
    english: 'The crocodile is in the river',
    words: ['sbk', 'm', 'itrw'],
    hieroglyphs: '𓆊𓅓𓇋𓏏𓂋𓏺𓈗𓈘𓈇',
    hieroglyphWords: ['𓆊', '𓅓', '𓇋𓏏𓂋𓏺𓈗𓈘𓈇'],
    category: 'Voyages',
    tags: ['phrase adverbiale', 'géographie'],
  },
  {
    original: 'Rꜥ m pt',
    french: 'Rê est dans le ciel',
    english: 'Ra is in the sky',
    words: ['Rꜥ', 'm', 'pt'],
    hieroglyphs: '𓇳𓅓𓊪𓏏𓇯',
    hieroglyphWords: ['𓇳', '𓅓', '𓊪𓏏𓇯'],
    category: 'Voyages',
    tags: ['phrase adverbiale', 'ciel'],
  },
  {
    original: 'pr Rꜥ',
    french: 'Le temple de Rê',
    english: 'The temple of Ra',
    words: ['pr', 'Rꜥ'],
    hieroglyphs: '𓉐𓇳',
    hieroglyphWords: ['𓉐', '𓇳'],
    category: 'Voyages',
    tags: ['génitif direct', 'ciel'],
  },
  {
    original: 'm hrw',
    french: 'De jour',
    english: 'By day',
    words: ['m', 'hrw'],
    hieroglyphs: '𓅓𓉔𓅱𓇳',
    hieroglyphWords: ['𓅓', '𓉔𓅱𓇳'],
    category: 'Voyages',
    tags: ['préposition', 'temps'],
  },
  {
    original: 'm grḥ',
    french: 'De nuit',
    english: 'By night',
    words: ['m', 'grḥ'],
    hieroglyphs: '𓅓𓇸',
    hieroglyphWords: ['𓅓', '𓇸'],
    category: 'Voyages',
    tags: ['préposition', 'temps'],
  },
  // ── Phrases thématiques par unité : chaque catégorie de phrases devient
  // sa propre étape du sentier (UNIT_SENTENCE_CATEGORIES). Unité 4
  // (Panthéon et royaume) : épithètes divines et titres royaux, en
  // apposition (« Osiris, le grand dieu ») ou en génitif (sꜣ Rꜥ).
  {
    original: 'ink sꜣ nsw',
    french: 'Moi, je suis le fils du roi',
    english: 'I am the king\'s son',
    words: ['ink', 'sꜣ', 'nsw'],
    hieroglyphs: '𓇋𓈖𓂓𓅭𓀀𓇓',
    hieroglyphWords: ['𓇋𓈖𓂓', '𓅭𓀀', '𓇓'],
    category: 'Présentation',
    tags: ['pronom indépendant', 'génitif direct'],
  },
  {
    original: 'ink ḥmt nsw',
    french: 'Moi, je suis l\'épouse du roi',
    english: 'I am the king\'s wife',
    words: ['ink', 'ḥmt', 'nsw'],
    hieroglyphs: '𓇋𓈖𓂓𓈞𓏏𓁐𓇓',
    hieroglyphWords: ['𓇋𓈖𓂓', '𓈞𓏏𓁐', '𓇓'],
    category: 'Présentation',
    tags: ['pronom indépendant', 'génitif direct'],
  },
  {
    original: 'ink sn',
    french: 'Moi, je suis un frère',
    english: 'I am a brother',
    words: ['ink', 'sn'],
    hieroglyphs: '𓇋𓈖𓂓𓋴𓈖𓀀',
    hieroglyphWords: ['𓇋𓈖𓂓', '𓋴𓈖𓀀'],
    category: 'Présentation',
    tags: ['pronom indépendant', 'famille'],
  },
  {
    original: 'ink sꜣ n zẖꜣw',
    french: 'Moi, je suis le fils du scribe',
    english: 'I am the scribe\'s son',
    words: ['ink', 'sꜣ', 'n', 'zẖꜣw'],
    hieroglyphs: '𓇋𓈖𓂓𓅭𓀀𓈖𓏞𓅱𓀀',
    hieroglyphWords: ['𓇋𓈖𓂓', '𓅭𓀀', '𓈖', '𓏞𓅱𓀀'],
    category: 'Présentation',
    tags: ['pronom indépendant', 'génitif indirect'],
  },
  {
    original: 'ḥmt.f pw',
    french: 'C\'est sa femme',
    english: 'It is his wife',
    words: ['ḥmt.f', 'pw'],
    hieroglyphs: '𓈞𓏏𓁐𓆑𓊪𓅱',
    hieroglyphWords: ['𓈞𓏏𓁐𓆑', '𓊪𓅱'],
    category: 'Famille et relations',
    tags: ['pronom suffixe', 'phrase nominale'],
  },
  {
    original: 'it.s m pr',
    french: 'Son père (à elle) est dans la maison',
    english: 'Her father is in the house',
    words: ['it.s', 'm', 'pr'],
    hieroglyphs: '𓇋𓏏𓀀𓋴𓅓𓉐',
    hieroglyphWords: ['𓇋𓏏𓀀𓋴', '𓅓', '𓉐'],
    category: 'Famille et relations',
    tags: ['pronom suffixe', 'phrase adverbiale'],
  },
  {
    original: 'it nṯr',
    french: 'Le père du dieu (un titre de prêtre)',
    english: 'The god\'s father (a priestly title)',
    words: ['it', 'nṯr'],
    hieroglyphs: '𓇋𓏏𓀀𓊹',
    hieroglyphWords: ['𓇋𓏏𓀀', '𓊹'],
    category: 'Famille et relations',
    tags: ['génitif direct', 'titre'],
  },
  {
    original: 'ꜥ nsw',
    french: 'Le bras du roi',
    english: 'The king\'s arm',
    words: ['ꜥ', 'nsw'],
    hieroglyphs: '𓂝𓇓',
    hieroglyphWords: ['𓂝', '𓇓'],
    category: 'Vie quotidienne',
    tags: ['génitif direct', 'corps'],
  },
  {
    original: 'jb.f nfr',
    french: 'Son bon cœur',
    english: 'His good heart',
    words: ['jb.f', 'nfr'],
    hieroglyphs: '𓄣𓆑𓄤',
    hieroglyphWords: ['𓄣𓆑', '𓄤'],
    category: 'Vie quotidienne',
    tags: ['pronom suffixe', 'corps'],
  },
  {
    original: 'bnr pw',
    french: 'Ce sont des dattes',
    english: 'These are dates',
    words: ['bnr', 'pw'],
    hieroglyphs: '𓃀𓈖𓂋𓊪𓅱',
    hieroglyphWords: ['𓃀𓈖𓂋', '𓊪𓅱'],
    category: 'Au marché',
    tags: ['phrase nominale', 'nourriture'],
  },
  {
    original: 'jrp n nsw',
    french: 'Du vin pour le roi',
    english: 'Wine for the king',
    words: ['jrp', 'n', 'nsw'],
    hieroglyphs: '𓇋𓂋𓊪𓈖𓇓',
    hieroglyphWords: ['𓇋𓂋𓊪', '𓈖', '𓇓'],
    category: 'Au marché',
    tags: ['préposition', 'nourriture'],
  },
  {
    original: 't n zẖꜣw',
    french: 'Du pain pour le scribe',
    english: 'Bread for the scribe',
    words: ['t', 'n', 'zẖꜣw'],
    hieroglyphs: '𓏏𓈖𓏞𓅱𓀀',
    hieroglyphWords: ['𓏏', '𓈖', '𓏞𓅱𓀀'],
    category: 'Au marché',
    tags: ['préposition', 'nourriture'],
  },
  {
    original: 'rmt m mw',
    french: 'Le poisson est dans l\'eau',
    english: 'The fish is in the water',
    words: ['rmt', 'm', 'mw'],
    hieroglyphs: '𓆠𓅓𓈖',
    hieroglyphWords: ['𓆠', '𓅓', '𓈖'],
    category: 'Au marché',
    tags: ['phrase adverbiale', 'nourriture'],
  },
  {
    original: 'jrt Ḥr',
    french: 'L\'œil d\'Horus',
    english: 'The Eye of Horus',
    words: ['jrt', 'Ḥr'],
    hieroglyphs: '𓂀𓏏𓏺𓎛𓁷𓂋𓅆',
    hieroglyphWords: ['𓂀𓏏𓏺', '𓎛𓁷𓂋𓅆'],
    category: 'Panthéon et royaume',
    tags: ['génitif direct', 'divinités'],
  },
  {
    original: 'Ḥr sꜣ Ỉs.t',
    french: 'Horus, fils d\'Isis',
    english: 'Horus, son of Isis',
    words: ['Ḥr', 'sꜣ', 'Ỉs.t'],
    hieroglyphs: '𓎛𓁷𓂋𓅆𓅭𓀀𓊨𓏏𓁐',
    hieroglyphWords: ['𓎛𓁷𓂋𓅆', '𓅭𓀀', '𓊨𓏏𓁐'],
    category: 'Panthéon et royaume',
    tags: ['apposition', 'divinités'],
  },
  {
    original: 'Ỉs.t mwt nṯr',
    french: 'Isis, la mère du dieu',
    english: 'Isis, the god\'s mother',
    words: ['Ỉs.t', 'mwt', 'nṯr'],
    hieroglyphs: '𓊨𓏏𓁐𓅐𓏏𓁐𓊹',
    hieroglyphWords: ['𓊨𓏏𓁐', '𓅐𓏏𓁐', '𓊹'],
    category: 'Panthéon et royaume',
    tags: ['apposition', 'divinités'],
  },
  {
    original: 'Wsỉr nṯr ꜥꜣ',
    french: 'Osiris, le grand dieu',
    english: 'Osiris, the great god',
    words: ['Wsỉr', 'nṯr', 'ꜥꜣ'],
    hieroglyphs: '𓊨𓁹𓊹𓉻',
    hieroglyphWords: ['𓊨𓁹', '𓊹', '𓉻'],
    category: 'Panthéon et royaume',
    tags: ['apposition', 'adjectif'],
  },
  {
    original: 'Rꜥ nṯr ꜥꜣ',
    french: 'Rê, le grand dieu',
    english: 'Ra, the great god',
    words: ['Rꜥ', 'nṯr', 'ꜥꜣ'],
    hieroglyphs: '𓇳𓊹𓉻',
    hieroglyphWords: ['𓇳', '𓊹', '𓉻'],
    category: 'Panthéon et royaume',
    tags: ['apposition', 'adjectif'],
  },
  {
    original: 'nṯr ꜥꜣ m pt',
    french: 'Le grand dieu est dans le ciel',
    english: 'The great god is in the sky',
    words: ['nṯr', 'ꜥꜣ', 'm', 'pt'],
    hieroglyphs: '𓊹𓉻𓅓𓊪𓏏𓇯',
    hieroglyphWords: ['𓊹', '𓉻', '𓅓', '𓊪𓏏𓇯'],
    category: 'Panthéon et royaume',
    tags: ['phrase adverbiale', 'adjectif'],
  },
  {
    original: 'Jmn m Wꜣst',
    french: 'Amon est à Thèbes',
    english: 'Amun is in Thebes',
    words: ['Jmn', 'm', 'Wꜣst'],
    hieroglyphs: '𓇋𓏠𓈖𓀭𓅓𓋆𓏏𓊖',
    hieroglyphWords: ['𓇋𓏠𓈖𓀭', '𓅓', '𓋆𓏏𓊖'],
    category: 'Panthéon et royaume',
    tags: ['phrase adverbiale', 'villes'],
  },
  {
    original: 'pr Jmn',
    french: 'Le temple d\'Amon',
    english: 'The temple of Amun',
    words: ['pr', 'Jmn'],
    hieroglyphs: '𓉐𓇋𓏠𓈖𓀭',
    hieroglyphWords: ['𓉐', '𓇋𓏠𓈖𓀭'],
    category: 'Panthéon et royaume',
    tags: ['génitif direct', 'divinités'],
  },
  {
    original: 'Ḏḥwtj pw',
    french: 'C\'est Thot',
    english: 'It is Thoth',
    words: ['Ḏḥwtj', 'pw'],
    hieroglyphs: '𓂧𓎛𓅝𓏏𓏭𓊪𓅱',
    hieroglyphWords: ['𓂧𓎛𓅝𓏏𓏭', '𓊪𓅱'],
    category: 'Panthéon et royaume',
    tags: ['phrase nominale', 'divinités'],
  },
  {
    original: 'Mꜣꜥt pw',
    french: 'C\'est Maât',
    english: 'It is Maat',
    words: ['Mꜣꜥt', 'pw'],
    hieroglyphs: '𓁦𓊪𓅱',
    hieroglyphWords: ['𓁦', '𓊪𓅱'],
    category: 'Panthéon et royaume',
    tags: ['phrase nominale', 'divinités'],
  },
  {
    original: 'sꜣ Rꜥ',
    french: 'Le fils de Rê (titre royal)',
    english: 'Son of Ra (royal title)',
    words: ['sꜣ', 'Rꜥ'],
    hieroglyphs: '𓅭𓀀𓇳',
    hieroglyphWords: ['𓅭𓀀', '𓇳'],
    category: 'Panthéon et royaume',
    tags: ['génitif direct', 'titre royal'],
  },
  {
    original: 'nsw bity',
    french: 'Roi de Haute et de Basse-Égypte',
    english: 'King of Upper and Lower Egypt',
    words: ['nsw', 'bity'],
    hieroglyphs: '𓇓𓆤',
    hieroglyphWords: ['𓇓', '𓆤'],
    category: 'Panthéon et royaume',
    tags: ['titre royal'],
  },
];

export default sentencesToConstruct;
